import { AuthStorage } from "utils/auth-storage";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Collapse,
  Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { WarehouseServices } from "apis/Services/WarehouseService";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import MaterialFormModal from "./materialFormModal";
import {
  getDestinationLocationOptions,
  getTransferType,
  isJobSiteLocation,
} from "./transfer-modal-utils";

const TransferModal = ({
  record,
  isTransferVisible,
  setIsTransferVisible,
  materialsOptions = [],
  setShouldReload = () => {},
  hasGranularPermissions,
  transferData = null,
  onSaveDraft = null,
}) => {
  const [form] = Form.useForm();
  const { company_id, id } = useParams();
  const [projects, setProjects] = useState();
  const [jobSitesOptions, setJobSitesOptions] = useState([]);
  const [subLocationOptions, setSubLocationOptions] = useState([]); // State for sub-location options
  const [fromLocationOptions, setFromLocationOptions] = useState([]);
  const [warehouseLocationOptions, setWarehouseLocationOptions] = useState([]);
  const [allFormErrors, setAllFormErrors] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [unitsOptions, setUnitsOptions] = useState([]);
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [localMaterialsOptions, setLocalMaterialsOptions] = useState([]);
  const [isMaterialModalVisible, setIsMaterialModalVisible] = useState(false);
  const [materialCreationTargetIndex, setMaterialCreationTargetIndex] =
    useState(null);

  // Use useWatch to make the component re-render when the 'items' field changes
  const items = Form.useWatch("items", form);
  const fromLocation = Form.useWatch("fromLocation", form);
  // Use useWatch to monitor the selected 'toLocation' (Destination Job Site)
  const toLocation = Form.useWatch("toLocation", form);

  // Determine if we're in edit mode
  const isEditMode = !!transferData;
  const isFromJobSite = isJobSiteLocation(fromLocation, jobSitesOptions);
  const isDestinationJobSite = isJobSiteLocation(
    toLocation,
    jobSitesOptions,
  );
  const destinationLocationOptions = getDestinationLocationOptions(
    fromLocation,
    warehouseLocationOptions,
    jobSitesOptions,
  );

  useEffect(() => {
    setLocalMaterialsOptions(
      Array.isArray(materialsOptions) ? materialsOptions : [],
    );
  }, [materialsOptions]);

  useEffect(() => {
    if (!isTransferVisible) {
      return;
    }

    if (
      categoryOptions.length > 0 ||
      subcategoryOptions.length > 0 ||
      unitsOptions.length > 0 ||
      locationsOptions.length > 0
    ) {
      return;
    }

    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      project: id,
      code,
      loginEmail: email,
    };

    WarehouseServices.getInventorySettings(ReqObj)
      .then((res) => {
        setCategoryOptions(res?.settingsDict?.categories || []);
        setSubcategoryOptions(res?.settingsDict?.subcategories || []);
        setLocationsOptions(res?.settingsDict?.locations || []);
        setUnitsOptions(res?.settingsDict?.units || []);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [
    categoryOptions.length,
    company_id,
    id,
    isTransferVisible,
    locationsOptions.length,
    subcategoryOptions.length,
    unitsOptions.length,
  ]);

  // --- Data Fetching and Initial Setup ---
  useEffect(() => {
    if (!projects) {
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");
      let ReqObj = {
        company: company_id,
        code,
        loginEmail: email,
      };
      WarehouseServices.getJobSites(ReqObj)
        .then((res) => {
          setProjects(res);
          if (res) {
            const options = res.map((item) => ({
              label: item.name || "Unnamed Project",
              value: item.name,
              jobSiteID: item.jobSiteID,
            }));
            setJobSitesOptions(options);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }

    // Handle initial form values
    if (transferData) {
      // Edit mode: populate form with transferData
      const formattedItems = transferData.items?.map((item) => ({
        itemID: Number(item.itemID),
        quantity: Number(item.quantity),
      })) || [{ itemID: undefined, quantity: undefined }];

      form.setFieldsValue({
        fromLocation: transferData.fromLocation,
        toLocation: transferData.toLocation,
        toSubLocation: transferData.toSubLocation,
        note: transferData.note,
        items: formattedItems,
      });

      // Set active keys to show all items initially
      setActiveKeys(formattedItems.map((_, index) => index));
    } else if (!record) {
      // New transfer mode
      form.setFieldsValue({
        fromLocation: undefined,
        toLocation: undefined,
        items: [{ itemID: undefined, quantity: undefined }],
        toSubLocation: undefined, // ensure it's cleared on new form
      });
      setActiveKeys([0]); // Make first item expanded by default
    } else {
      // Using record (legacy mode)
      form.setFieldsValue({
        ...record,
        items: [{ itemID: record.itemID, quantity: undefined }] || [
          { itemID: undefined, quantity: undefined },
        ],
        toSubLocation: undefined, // ensure it's cleared on new form
      });
      setActiveKeys([0]); // Make first item expanded by default
    }
  }, [record, transferData, form, projects, company_id]);

  // --- Dynamic Sub-Location Options Effect ---
  useEffect(() => {
    if (!isDestinationJobSite) {
      setSubLocationOptions([]);
      form.setFieldsValue({ toSubLocation: undefined });
      return;
    }

    if (toLocation && projects) {
      const selectedJobSite = projects.find((p) => p.name === toLocation);
      const subLocations = selectedJobSite?.subLocationNames || [];

      const options = subLocations.map((subName) => ({
        label: subName,
        value: subName,
      }));
      setSubLocationOptions(options);

      // If the currently selected sub-location is no longer valid, clear it
      const currentSubLocation = form.getFieldValue("toSubLocation");
      if (currentSubLocation && !subLocations.includes(currentSubLocation)) {
        form.setFieldsValue({ toSubLocation: undefined });
      }
    } else {
      setSubLocationOptions([]);
      form.setFieldsValue({ toSubLocation: undefined });
    }
  }, [isDestinationJobSite, toLocation, projects, form]);

  // --- Dynamic From Location Options Effect (Existing Logic) ---
  useEffect(() => {
    // Get a set of locations from the currently selected materials
    const selectedItemIDs = (items || [])
      .map((item) => item?.itemID)
      .filter((id) => id);
    const selectedMaterialLocations = new Set(
      localMaterialsOptions
        .filter((mat) => selectedItemIDs.includes(mat.itemID))
        .map((mat) => mat.storageLocation)
        .filter((loc) => loc),
    );

    // Get a list of all unique possible locations
    const allLocations = [
      ...new Set(
        localMaterialsOptions
          .map((mat) => mat.storageLocation)
          .filter((loc) => loc),
      ),
    ];

    setWarehouseLocationOptions(
      allLocations.map((location) => ({
        label: location,
        value: location,
      })),
    );

    const warehouseOptions = allLocations.map((location) => ({
      label: location,
      value: location,
      disabled: !selectedMaterialLocations.has(location),
    }));

    setFromLocationOptions([
      { label: "Warehouse Locations", options: warehouseOptions },
      { label: "Job Sites", options: jobSitesOptions },
    ]);

    // If the currently selected 'fromLocation' is no longer valid, clear it
    const currentFromLocation = form.getFieldValue("fromLocation");
    const isJobSiteFromLocation = jobSitesOptions.some(
      (jobSite) => jobSite.value === currentFromLocation,
    );
    if (
      currentFromLocation &&
      !isJobSiteFromLocation &&
      !selectedMaterialLocations.has(currentFromLocation)
    ) {
      form.setFieldsValue({ fromLocation: undefined });
    }
  }, [form, items, jobSitesOptions, localMaterialsOptions]);

  // Helper function to get jobSiteID from the selected toLocation
  const getJobSiteID = (toLocationName) => {
    const selectedJobSite = jobSitesOptions.find(
      (option) => option.value === toLocationName,
    );
    return selectedJobSite?.jobSiteID?.toString() || "";
  };

  // --- Form Submission Logic ---
  const onSubmit = (values) => {
    const transferType = getTransferType(
      values.fromLocation,
      values.toLocation,
      jobSitesOptions,
    );
    const destinationIsJobSite = isJobSiteLocation(
      values.toLocation,
      jobSitesOptions,
    );
    const transformedValues = {
      fromLocation: values.fromLocation,
      toLocation: values.toLocation,
      toLocationID: destinationIsJobSite
        ? getJobSiteID(values.toLocation)
        : "",
      note: values.note || "",
      toSubLocation: destinationIsJobSite
        ? values.toSubLocation || ""
        : "",
      transferType,
      items: values.items.map((item) => ({
        itemID: item.itemID,
        quantity: item.quantity.toString(),
      })),
    };
    if (isEditMode && transferData?.transferID) {
      transformedValues.transferID = transferData.transferID;
    }

    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
      values: isEditMode ? [transformedValues] : transformedValues,
    };

    // If in edit mode and transferData has an ID, include it
    if (isEditMode && transferData.id) {
      ReqObj.transferId = transferData.id;
    }

    let serviceMethod;

    if (isEditMode) {
      serviceMethod = WarehouseServices.updateTransferDrafts(ReqObj);
    } else if (hasGranularPermissions) {
      serviceMethod = WarehouseServices.transferRequest(ReqObj);
    } else {
      serviceMethod = WarehouseServices.transferInventoryItem(ReqObj);
    }

    const successMessage = hasGranularPermissions
      ? "Request sent successfully"
      : isEditMode
        ? "Transfer updated successfully"
        : "Transfer created successfully";

    setIsSubmittingTransfer(true);

    serviceMethod
      .then((res) => {
        toast.success(successMessage);
        setShouldReload(true);
        setIsTransferVisible(false);
        form.resetFields();
        setAllFormErrors([]); // Clear errors on success
      })
      .catch((err) => {
        console.log(err);
        toast.error("Something went wrong, please try again.");
      })
      .finally(() => {
        setIsSubmittingTransfer(false);
      });
  };

  // --- Save as Draft Logic ---
  const handleSaveDraft = async () => {
    if (!onSaveDraft) {
      console.warn("onSaveDraft callback not provided");
      return;
    }

    if (isFromJobSite) {
      toast.error("Transfers from job sites cannot be saved as drafts");
      return;
    }

    setIsSavingDraft(true);

    try {
      const values = await form.validateFields();
      const destinationIsJobSite = isJobSiteLocation(
        values.toLocation,
        jobSitesOptions,
      );
      const transformedValues = {
        fromLocation: values.fromLocation,
        toLocation: values.toLocation,
        toLocationID: destinationIsJobSite
          ? getJobSiteID(values.toLocation)
          : "",
        note: values.note || "",
        toSubLocation: destinationIsJobSite
          ? values.toSubLocation || ""
          : "",
        transferType: getTransferType(
          values.fromLocation,
          values.toLocation,
          jobSitesOptions,
        ),
        items: values.items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity.toString(),
        })),
        isDraft: true,
      };

      // add transferItemID to items if editMode

      // If in edit mode and transferData has an ID, include it
      if (isEditMode && transferData.id) {
        transformedValues.id = transferData.id;
      }

      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");

      const ReqObj = {
        company: company_id,
        code,
        loginEmail: email,
        values: transformedValues,
      };

      await WarehouseServices.transferDraft(ReqObj);
      toast.success("Transfer saved in drafts successfully");
      setShouldReload(true);
      setIsTransferVisible(false);
      form.resetFields();
      setAllFormErrors([]);
    } catch (error) {
      if (error.errorFields) {
        // Validation errors
        setAllFormErrors(error.errorFields);
        toast.error("Please fill in all required fields");
      } else {
        // API or other errors
        console.error("Error saving draft:", error);
        toast.error("Failed to save draft. Please try again.");
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  const onCancel = () => {
    setIsTransferVisible(false);
    form.resetFields();
    form.setFieldsValue({
      items: [{ itemID: undefined, quantity: undefined }],
      toSubLocation: undefined, // Reset sub-location
    });
    setAllFormErrors([]); // Clear errors when modal is closed
    setActiveKeys([0]); // Reset to first item expanded
  };

  const handleFinish = (values) => {
    onSubmit(values);
  };

  const handleFinishFailed = (errorInfo) => {
    setAllFormErrors(errorInfo.errorFields);
  };

  const getMaterialByItemID = (itemID) => {
    return localMaterialsOptions.find((mat) => mat.itemID === itemID);
  };

  const getAvailableMaterials = (currentIndex) => {
    const selectedItemIDs =
      form
        .getFieldValue("items")
        ?.map((item, index) => (index !== currentIndex ? item?.itemID : null))
        .filter((id) => id) || [];

    return localMaterialsOptions.filter(
      (mat) => !selectedItemIDs.includes(mat.itemID),
    );
  };

  const normalizeCreatedItemID = (value) => {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? value : parsedValue;
  };

  const extractCreatedItemID = (response) => {
    return (
      response?.itemID ??
      response?.data?.itemID ??
      response?.content?.itemID ??
      response?.values?.itemID
    );
  };

  const handleCreateMaterial = async (values) => {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
      values,
    };

    try {
      const res = await WarehouseServices.createWarehouseItem(ReqObj);
      const createdItemID = extractCreatedItemID(res);

      if (!createdItemID) {
        toast.error("Material created, but no item ID was returned.");
        return;
      }

      const normalizedItemID = normalizeCreatedItemID(createdItemID);
      const createdMaterial = {
        ...values,
        itemID: normalizedItemID,
      };

      setLocalMaterialsOptions((prev) => {
        const existingWithoutNewItem = prev.filter(
          (item) => String(item.itemID) !== String(normalizedItemID),
        );

        return [...existingWithoutNewItem, createdMaterial];
      });

      if (materialCreationTargetIndex !== null) {
        const currentItems = form.getFieldValue("items") || [];
        const updatedItems = currentItems.map((item, index) =>
          index === materialCreationTargetIndex
            ? { ...item, itemID: normalizedItemID }
            : item,
        );

        form.setFieldsValue({
          items: updatedItems,
        });
      }

      setShouldReload(true);
      setIsMaterialModalVisible(false);
      setMaterialCreationTargetIndex(null);
      toast.success("Material created!");
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    }
  };

  const handleOpenCreateMaterialModal = (targetIndex) => {
    setMaterialCreationTargetIndex(targetIndex);
    setIsMaterialModalVisible(true);
  };

  const handleCloseCreateMaterialModal = () => {
    setMaterialCreationTargetIndex(null);
    setIsMaterialModalVisible(false);
  };

  // New function to check if a specific item has errors based on the full error list
  const hasItemErrors = (itemIndex) => {
    return allFormErrors.some(
      (error) =>
        error.name &&
        error.name[0] === "items" &&
        error.name[1] === itemIndex &&
        error.errors.length > 0,
    );
  };

  // Filter function for Select components
  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  // Determine modal title
  const getModalTitle = () => {
    if (isEditMode) {
      return "Edit Draft Transfer";
    }
    return hasGranularPermissions
      ? "Request New Transfer"
      : "Create New Transfer";
  };

  return (
    <Modal
      title={getModalTitle()}
      open={isTransferVisible}
      centered
      onCancel={onCancel}
      width={800}
      footer={[
        onSaveDraft && !isEditMode && (
          <Button
            key="draft"
            onClick={handleSaveDraft}
            loading={isSavingDraft}
            disabled={isFromJobSite}
            style={{ marginRight: "auto" }}
          >
            Save as Draft
          </Button>
        ),
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmittingTransfer}
          onClick={() => {
            form.submit();
          }}
        >
          {hasGranularPermissions
            ? "Send Request"
            : isEditMode
              ? "Update Draft"
              : "Create Transfer"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
      >
        <Form.Item label="Materials">
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                <Collapse
                  activeKey={activeKeys}
                  onChange={setActiveKeys}
                  ghost
                  className=" bg-light bg-opacity-75 border border-1 mb-3"
                >
                  {fields.map(({ key, name, ...restField }) => {
                    const currentItem = form.getFieldValue(["items", name]);
                    const selectedMaterial = currentItem?.itemID
                      ? getMaterialByItemID(currentItem.itemID)
                      : null;

                    const itemHasErrors = hasItemErrors(name);

                    return (
                      <Collapse.Panel
                        key={key}
                        header={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <span
                              className={
                                itemHasErrors ? "text-danger fw-bold" : ""
                              }
                            >
                              {selectedMaterial
                                ? `${selectedMaterial.materialName} (${selectedMaterial.sku})`
                                : `Material ${name + 1}`}
                            </span>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCreateMaterialModal(name);
                                }}
                                style={{
                                  fontWeight: 500,
                                }}
                              >
                                Create Material
                              </Button>

                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    remove(name);
                                    // Remove the key from activeKeys when removing an item
                                    setActiveKeys((prev) =>
                                      prev.filter((k) => k !== key),
                                    );
                                  }}
                                  size="small"
                                  style={{ marginLeft: 4 }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        }
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Form.Item
                            {...restField}
                            name={[name, "itemID"]}
                            label="Material"
                            rules={[
                              {
                                required: true,
                                message: "Please select a material",
                              },
                            ]}
                            style={{ marginBottom: 12 }}
                          >
                            <Select
                              placeholder="Select material"
                              showSearch
                              filterOption={filterOption}
                              optionFilterProp="label"
                            >
                              {getAvailableMaterials(name).map((mat) => (
                                <Select.Option
                                  key={mat.itemID}
                                  value={mat.itemID}
                                  label={`${mat.materialName} (${mat.sku})`}
                                >
                                  {mat.materialName} ({mat.sku})
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          {selectedMaterial && (
                            <div className=" bg-body rounded-2 p-3 d-flex flex-column gap-0 mb-3">
                              <strong>
                                Available Stock: {selectedMaterial.currentStock}{" "}
                                units
                              </strong>
                              {selectedMaterial.itemVendor && (
                                <span className="opacity-75">
                                  Vendor: {selectedMaterial.itemVendor}
                                </span>
                              )}
                              <span className="opacity-75">
                                Location: {selectedMaterial.storageLocation}
                              </span>
                            </div>
                          )}

                          <Form.Item
                            {...restField}
                            name={[name, "quantity"]}
                            label="Quantity"
                            rules={[
                              {
                                required: true,
                                message: "Please enter quantity",
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              min={1}
                              max={selectedMaterial?.currentStock}
                              style={{ width: "100%" }}
                              placeholder="Enter quantity"
                            />
                          </Form.Item>
                        </Space>
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      const newIndex = fields.length;
                      add({ itemID: undefined, quantity: undefined });
                      // Add the new item key to activeKeys to make it expanded
                      setActiveKeys((prev) => [...prev, newIndex]);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Another Material
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item
          label="From Location"
          name="fromLocation"
          rules={[
            {
              required: true,
              message: "Please select a from location",
            },
          ]}
        >
          <Select
            placeholder="Select a warehouse location or job site"
            options={fromLocationOptions}
            showSearch
            filterOption={filterOption}
            optionFilterProp="label"
            onChange={() => {
              form.setFieldsValue({
                toLocation: undefined,
                toSubLocation: undefined,
              });
            }}
          />
        </Form.Item>

        <Form.Item
          label="Destination"
          name="toLocation"
          rules={[
            {
              required: true,
              message: "Please select a warehouse location or job site",
            },
          ]}
        >
          <Select
            placeholder="Select a warehouse location or job site"
            options={destinationLocationOptions}
            showSearch
            filterOption={filterOption}
            optionFilterProp="label"
            disabled={!fromLocation}
          />
        </Form.Item>

        {isDestinationJobSite && (
          <Tooltip
            title={
              !toLocation
                ? "Select a Destination Job Site first"
                : subLocationOptions.length === 0
                  ? "This destination has no Sub-Locations"
                  : ""
            }
          >
            <Form.Item
              label="Destination Sub-Location (Optional)"
              name="toSubLocation"
            >
              <Select
                placeholder="Select sub-location"
                options={subLocationOptions}
                showSearch
                filterOption={filterOption}
                optionFilterProp="label"
                disabled={!toLocation || subLocationOptions.length === 0}
              />
            </Form.Item>
          </Tooltip>
        )}

        <Form.Item label="Notes (Optional)" name="note">
          <Input.TextArea rows={3} placeholder="Please include need by date" />
        </Form.Item>
      </Form>

      <MaterialFormModal
        open={isMaterialModalVisible}
        isEditing={false}
        categoryOptions={categoryOptions}
        subcategoryOptions={subcategoryOptions}
        unitsOptions={unitsOptions}
        locationsOptions={locationsOptions}
        onCancel={handleCloseCreateMaterialModal}
        onSubmit={handleCreateMaterial}
      />
    </Modal>
  );
};

export default TransferModal;
